import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { trackContact } from '../utils/supabaseEdgeApi';

const getMinimumDate = () => {
  const minimumDate = new Date();
  minimumDate.setDate(minimumDate.getDate() + 1);
  const year = minimumDate.getFullYear();
  const month = String(minimumDate.getMonth() + 1).padStart(2, '0');
  const day = String(minimumDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const QuickBookingModal = ({ open, onClose, poojaName = '', packageName = '', requestType = 'booking' }) => {
  const [form, setForm] = useState({
    pooja: poojaName,
    package: packageName,
    date: '',
    name: '',
    contact: '',
    location: '',
    lat: '',
    lng: '',
  });
  const [preference, setPreference] = useState('whatsapp');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({
        pooja: poojaName,
        package: packageName,
        date: '',
        name: '',
        contact: '',
        location: '',
        lat: '',
        lng: '',
      });
      setPreference('whatsapp');
      setLocationError('');
      setFormError('');
    }
  }, [open, poojaName, packageName]);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Location access is not supported by this browser. Enter your address manually.');
      return;
    }

    setLocationError('');
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { latitude, longitude } = coords;
        setForm((previous) => ({ ...previous, lat: latitude, lng: longitude }));
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
          );
          if (!response.ok) {
            throw new Error('Unable to look up your address. Please enter it manually.');
          }
          const result = await response.json();
          if (!result?.display_name) {
            throw new Error('No address was found for your current location. Please enter it manually.');
          }
          setForm((previous) => ({
            ...previous,
            location: result.display_name,
          }));
        } catch (error) {
          setLocationError(error.message || 'Unable to fetch your address. Please enter it manually.');
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        setLocationLoading(false);
        setLocationError(
          error.code === 1
            ? 'Location permission was denied. Allow access or enter your address manually.'
            : 'Unable to fetch your location. Please try again or enter your address manually.'
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      setFormError('Please enter your name and contact number.');
      return;
    }
    if (form.date && form.date < getMinimumDate()) {
      setFormError('Booking dates must be tomorrow or later.');
      return;
    }

    setFormError('');
    const date = form.date ? form.date.split('-').reverse().join('/') : '[Please add your preferred date]';
    const selectedPooja = form.pooja.trim() || '[Please add the pooja name]';
    const locationLink = form.lat !== '' && form.lng !== ''
      ? `https://maps.google.com/?q=${form.lat},${form.lng}`
      : '';
    const requestMessage = requestType === 'guidance'
      ? `I would like guidance about choosing a pooja for my family's mental and physical well-being and good growth.`
      : `I would like to book a pooja for my family's mental and physical well-being and good growth.`;
    const message = [
      requestMessage,
      '',
      `Pooja: ${selectedPooja}`,
      `Package: ${form.package.trim() || '[Please add your preferred package]'}`,
      `My name is: ${form.name.trim()}`,
      `Contact: ${form.contact.trim()}`,
      `Date: ${date}`,
      `Location: ${form.location.trim() || '[Please add your location]'}`,
      `Location link: ${locationLink || '[Please add a map link if available]'}`,
    ].join('\n');

    if (preference === 'whatsapp') {
      trackContact('whatsapp');
      window.open(`https://wa.me/919986144114?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    } else {
      trackContact('email');
      const subject = encodeURIComponent(
        `${requestType === 'guidance' ? 'Pooja guidance request' : 'Pooja booking request'} - ${form.pooja || 'Vedamarga'}`
      );
      window.location.href = `mailto:vedamarga1@gmail.com?subject=${subject}&body=${encodeURIComponent(message)}`;
    }
    onClose();
  };
  const dateIsInvalid = Boolean(form.date && form.date < getMinimumDate());

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>
          {requestType === 'guidance' ? 'Get guidance about a pooja' : 'Book a pooja'}
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1, pt: '8px !important' }}>
          <TextField
            label="Pooja"
            name="pooja"
            value={form.pooja}
            onChange={handleChange}
            placeholder="Tell us which pooja or guidance you need"
            fullWidth
            InputProps={poojaName ? { readOnly: true } : undefined}
          />
          <TextField
            label="Package"
            name="package"
            value={form.package}
            onChange={handleChange}
            placeholder="Optional"
            fullWidth
            InputProps={packageName ? { readOnly: true } : undefined}
          />
          <TextField
            label="Date"
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            inputProps={{ min: getMinimumDate() }}
            InputLabelProps={{ shrink: true }}
            error={dateIsInvalid}
            helperText={dateIsInvalid ? 'Booking dates must be tomorrow or later.' : 'Optional. If provided, choose tomorrow or a later date.'}
            fullWidth
          />
          <TextField
            label="Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            error={!!formError && !form.name.trim()}
            fullWidth
          />
          <TextField
            label="Contact number"
            name="contact"
            type="tel"
            value={form.contact}
            onChange={handleChange}
            required
            error={!!formError && !form.contact.trim()}
            fullWidth
          />
          <TextField
            label="Address / current location"
            name="location"
            value={form.location}
            onChange={(event) => {
              setForm((previous) => ({
                ...previous,
                location: event.target.value,
                lat: '',
                lng: '',
              }));
              setLocationError('');
            }}
            multiline
            minRows={2}
            fullWidth
          />
          {form.lat !== '' && form.lng !== '' ? (
            <Typography variant="body2">
              Map link: https://maps.google.com/?q={form.lat},{form.lng}
            </Typography>
          ) : null}
          <Box>
            <Button
              type="button"
              variant="outlined"
              onClick={fetchCurrentLocation}
              disabled={locationLoading}
            >
              {locationLoading ? 'Fetching location...' : 'Use current location'}
            </Button>
            {locationError ? (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {locationError}
              </Typography>
            ) : null}
          </Box>
          <FormControl>
            <FormLabel>Send your request using</FormLabel>
            <RadioGroup
              row
              name="contactPreference"
              value={preference}
              onChange={(event) => {
                setPreference(event.target.value);
              }}
            >
              <FormControlLabel value="whatsapp" control={<Radio />} label="WhatsApp" />
              <FormControlLabel value="email" control={<Radio />} label="Email" />
            </RadioGroup>
          </FormControl>
          {formError ? <Typography color="error" variant="body2">{formError}</Typography> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="error">
            {requestType === 'guidance' ? 'Get guidance via' : 'Continue to'} {preference === 'email' ? 'email' : 'WhatsApp'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default QuickBookingModal;
